import { HttpEvent } from '@angular/common/http';
import { Type } from '@angular/core';
import * as Rx from 'rxjs';
import { Collections } from '../collections.model';
import { BackendErrorResponse, BackendPushResponse } from './backend';
import { DatastoreCollection } from './datastore-collection';
import { DatastoreDocument } from './datastore-document';
import { DocumentQuery, NullQuery, Query } from './query';
import { RequestDataOptions } from './request-data';

export interface DatastoreInterface {
  /**
   * Returns a collection of objects, customised by query.
   *
   * @param collectionName Collection name
   * @param queryFn Query to refine results
   */
  collection<K extends keyof Collections>(
    collectionName: K,
    queryFn?: (q: Query<K>) => Query<K> | Rx.Observable<Query<K> | NullQuery>,
  ): DatastoreCollection<K>;

  /**
   * Returns a single document from a collection specified by id.
   * If `documentId` is an observable, new objects may be fetched from the
   * network and emitted as they arrive. Note that if it emits too quickly,
   * such that the network request has not completed before the id changes,
   * that object with the previous id will not be emitted.
   *
   * @param collectionName Collection name
   * @param documentId$ If not provided, defaults to the first document of the collection.
   * Omitting this is useful for logged-out requests when document ID is not applicable
   * @param query Only required if querying by a unique secondary ID
   */
  document<K extends keyof Collections>(
    collectionName: K,
    documentId$?:
      | string
      | number
      | Rx.Observable<string>
      | Rx.Observable<number>,
  ): DatastoreDocument<K>;
  document<K extends keyof Collections, OtherId extends keyof Collections[K]>(
    collectionName: K,
    documentSecondaryId$:
      | Collections[K][OtherId]
      | Rx.Observable<Collections[K][OtherId]>,
    documentQuery$:
      | DocumentQuery<K, OtherId>
      | Rx.Observable<DocumentQuery<K, OtherId> | undefined>,
  ): DatastoreDocument<K>;

  /**
   * Returns a list of objects from a collection specified by id.
   * If an observable is passed in for `objectIds` then only the delta will be
   * refetched from the network.
   *
   * @param collectionName Collection name
   * @param objectIds$
   */
  objects<K extends keyof Collections>(
    collectionName: K,
    objectIds$:
      | ReadonlyArray<number>
      | ReadonlyArray<string>
      | Rx.Observable<ReadonlyArray<number>>
      | Rx.Observable<ReadonlyArray<string>>,
  ): Rx.Observable<ReadonlyArray<Collections[K]>>;

  /**
   * Creates a single object.
   */
  createObject<K extends keyof Collections>(
    collectionName: K,
    object: Collections[K],
    extra?: { readonly [index: string]: string | number },
  ): Promise<BackendPushResponse<K>>;
}

/** Auth user ID for logged-out use of the datastore */
export const LOGGED_OUT_KEY = '0';

export interface DatastoreConfig {
  readonly webSocketUrl: string;
  readonly enableStoreFreeze: boolean;
  readonly httpAdapter: Type<HttpAdapter>;
  readonly requestData: RequestDataOptions;
}

export interface SuccessResponseData<T> {
  readonly status: 'success';
  readonly result: T;
  readonly requestId?: string;
}

export type ErrorResponseData<E> = BackendErrorResponse<E>;

export type ResponseData<T, E> = SuccessResponseData<T> | ErrorResponseData<E>;

export interface HttpAdapter {
  get<T, E>(endpoint: string, options?: {}): Rx.Observable<ResponseData<T, E>>;
  get<T, E>(
    endpoint: string,
    options?: {},
  ): Rx.Observable<HttpEvent<ResponseData<T, E>>>;

  post<T, E>(
    endpoint: string,
    body: any | null,
    options?: {},
  ): Rx.Observable<ResponseData<T, E>>;
  post<T, E>(
    endpoint: string,
    body: any | null,
    options?: {},
  ): Rx.Observable<HttpEvent<ResponseData<T, E>>>;

  put<T, E>(
    endpoint: string,
    body: any | null,
    options?: {},
  ): Rx.Observable<ResponseData<T, E>>;
  put<T, E>(
    endpoint: string,
    body: any | null,
    options?: {},
  ): Rx.Observable<HttpEvent<ResponseData<T, E>>>;

  delete<T, E>(
    endpoint: string,
    options?: {},
  ): Rx.Observable<ResponseData<T, E>>;
  delete<T, E>(
    endpoint: string,
    options?: {},
  ): Rx.Observable<HttpEvent<ResponseData<T, E>>>;
}
